import { useEffect, useState, useRef } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "@api";
import './DirectoryResults.css';
import LeagueCard from '../../Components/LeagueCard/LeagueCard.jsx';
import LeagueListItem from "../../Components/LeagueListItem/LeagueListItem.jsx";
import DirectoryResultsFilters from './DirectoryResultsFilters';
import { US_STATES } from "../../../../utils/usStates";
import { Helmet } from "react-helmet-async";

function DirectoryResults({ allSports, allAudiences, allAges, allPrograms }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preloadedResults = location.state?.preloadedDirectoryResults;
  const [hasLoadedOnce, setHasLoadedOnce] = useState(Boolean(preloadedResults));
  const resultsHeaderRef = useRef(null);
  const didMountPageScrollRef = useRef(false);
  const [directoryLeagues, setDirectoryLeagues] = useState(
    preloadedResults?.leagues || []
  );
  const [loading, setLoading] = useState(!preloadedResults);
  const [availableStates, setAvailableStates] = useState([]);
  const [scopedFilters, setScopedFilters] = useState({
    sports: allSports || [],
    audiences: allAudiences || [],
    age_groups: allAges || [],
    program_types: allPrograms || [],
  });
  const intent =
    location.pathname === "/sport" || location.pathname.startsWith("/sport/")
      ? "sport"
      : location.pathname === "/audience" || location.pathname.startsWith("/audience/")
        ? "audience"
        : location.pathname.includes("/audience")
          ? "audience"
          : location.pathname.includes("/sport")
            ? "sport"
            : searchParams.get("intent");



  const pathState = location.pathname.startsWith("/state/")
    ? location.pathname.split("/")[2] || ""
    : "";

  const pathCounty = location.pathname.startsWith("/county/")
    ? (location.pathname.split("/")[2] || "").replace(/-/g, " ")
    : location.pathname.startsWith("/state/") &&
      location.pathname.split("/")[3] === "county"
      ? (location.pathname.split("/")[4] || "").replace(/-/g, " ")
      : "";

  const state = pathState || searchParams.get("state");
  const county = pathCounty || searchParams.get("county");

  const pathSport =
    location.pathname.startsWith("/state/") &&
      location.pathname.split("/")[3] === "county" &&
      location.pathname.split("/")[5] === "sport"
      ? location.pathname.split("/")[6] || ""
      : location.pathname.startsWith("/state/") &&
        location.pathname.split("/")[3] === "sport"
        ? location.pathname.split("/")[4] || ""
        : location.pathname.startsWith("/county/") &&
          location.pathname.split("/")[3] === "sport"
          ? location.pathname.split("/")[4] || ""
          : location.pathname.startsWith("/sport/") &&
            location.pathname.split("/")[2] !== "audience"
            ? location.pathname.split("/")[2] || ""
            : "";

  const pathAudience =
    location.pathname.startsWith("/state/") &&
      location.pathname.split("/")[3] === "county" &&
      location.pathname.split("/")[5] === "sport" &&
      location.pathname.split("/")[7] === "audience"
      ? location.pathname.split("/")[8] || ""
      : location.pathname.startsWith("/state/") &&
        location.pathname.split("/")[3] === "sport" &&
        location.pathname.split("/")[5] === "audience"
        ? location.pathname.split("/")[6] || ""
        : location.pathname.startsWith("/state/") &&
          location.pathname.split("/")[3] === "county" &&
          location.pathname.split("/")[5] === "audience"
          ? location.pathname.split("/")[6] || ""
          : location.pathname.startsWith("/state/") &&
            location.pathname.split("/")[3] === "audience"
            ? location.pathname.split("/")[4] || ""
            : location.pathname.startsWith("/sport/") &&
              location.pathname.split("/")[3] === "audience"
              ? location.pathname.split("/")[4] || ""
              : location.pathname.startsWith("/audience/")
                ? location.pathname.split("/")[2] || ""
                : "";

  const sport = pathSport ? [pathSport] : searchParams.getAll("sport") || [];
  const verified = searchParams.get("verified");
  const gender = pathAudience ? [pathAudience] : searchParams.getAll("gender");
  const competition_level = searchParams.getAll("competition_level") || [];
  const age = searchParams.getAll("age") || [];
  const type = searchParams.getAll("type") || [];

  const page = Number(searchParams.get("page") || 1);
  const [totalPages, setTotalPages] = useState(preloadedResults?.totalPages || 1);
  const audience = gender;


  const isSportPath =
    location.pathname === "/sport" || location.pathname.startsWith("/sport/");

  const isStateSportPath =
    location.pathname.startsWith("/state/") && location.pathname.includes("/sport");
  const isStateAudiencePath =
    location.pathname.startsWith("/state/") && location.pathname.includes("/audience");
  const isCountySportPath =
    location.pathname.startsWith("/county/") && location.pathname.includes("/sport");
  const isSportAudiencePath =
    location.pathname.startsWith("/sport/") &&
    location.pathname.split("/")[3] === "audience";
  const isStateCountySportPath =
    location.pathname.startsWith("/state/") &&
    location.pathname.includes("/county/") &&
    location.pathname.includes("/sport");
  const isStateCountyAudiencePath =
    location.pathname.startsWith("/state/") &&
    location.pathname.includes("/county/") &&
    location.pathname.includes("/audience");

  const hasIntent = Boolean(
    isSportPath ||
    isStateSportPath ||
    isStateAudiencePath ||
    isCountySportPath ||
    isSportAudiencePath ||
    isStateCountySportPath ||
    isStateCountyAudiencePath ||
    state ||
    county ||
    sport.length ||
    gender.length ||
    age.length ||
    type.length ||
    competition_level.length ||
    intent
  );

  const [viewMode, setViewMode] = useState("list"); // "grid" | "list"
  const [totalCount, setTotalCount] = useState(preloadedResults?.total || 0);
  const limit = Number(searchParams.get("limit") || 10);
  const q = searchParams.get("q") || "";

  const buildPageSearch = (pageNum) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", pageNum);
    return `${location.pathname}?${params.toString()}`;
  };
  const buildPageSearchWithLimit = (pageNum, nextLimit = limit) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", pageNum);
    params.set("limit", String(nextLimit));
    return `${location.pathname}?${params.toString()}`;
  };
  const filterKey = JSON.stringify({
    pathname: location.pathname,
    state,
    county,
    sport,
    gender,
    age,
    type,
    competition_level,
    verified,
    q,
  });
  useEffect(() => {
    if (page === 1) return;
    const params = new URLSearchParams(searchParams);
    params.set("page", "1");
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [filterKey]);

  useEffect(() => {
    if (!didMountPageScrollRef.current) {
      didMountPageScrollRef.current = true;
      return;
    }

    if (!hasLoadedOnce) return;
    if (!resultsHeaderRef.current) return;

    resultsHeaderRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [page]);

  const getStateName = (code) => {
    const match = US_STATES.find(
      (s) => s.code === code?.toUpperCase()
    );
    return match ? match.name : "";
  };

  useEffect(() => {
    if (!hasIntent) return;

    if (preloadedResults && page === 1 && limit === preloadedResults.limit) {
      return;
    }

    const fetchDirectoryLeagues = async () => {
      try {
        setLoading(true);
        const params = {};
        const country = (searchParams.get("country") || "US").toUpperCase();
        params.country = country;

        if (state) params.state = state.toUpperCase();
        if (county) params.county = county;
        if (sport.length > 0) params.sport = sport;
        if (gender.length > 0) params.gender = gender;
        if (age.length > 0) params.age = age;
        if (type.length > 0) params.type = type;
        if (competition_level.length > 0) params.competition_level = competition_level;
        if (verified === "true") params.verified = "true";
        if (q.trim()) params.q = q.trim();
        params.page = page;
        params.limit = limit;

        const res = await api.get("/api/directory/leagues", { params });

        const leagues = res.data.leagues || [];

        const sortedLeagues = [...leagues].sort((a, b) => {
          const aIsFastAction = a.slug === "fastactionbasketball" || a.name?.toLowerCase().includes("fast action");
          const bIsFastAction = b.slug === "fastactionbasketball" || b.name?.toLowerCase().includes("fast action");

          if (aIsFastAction && !bIsFastAction) return -1;
          if (!aIsFastAction && bIsFastAction) return 1;
          return 0;
        });

        setDirectoryLeagues(sortedLeagues);
        setTotalPages(res.data.totalPages || 1);
        setTotalCount(res.data.total || 0);
        if ((res.data.totalPages || 1) < page && page > 1) {
          const params = new URLSearchParams(searchParams);
          params.set("page", "1");
          navigate(`${location.pathname}?${params.toString()}`, { replace: true });
          return;
        }
      } catch (err) {
        console.error("Failed to fetch directory leagues:", err);
        setDirectoryLeagues([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setHasLoadedOnce(true);
        setLoading(false);
      }
    };


    fetchDirectoryLeagues();
  }, [
    location.pathname,
    state,
    county,
    sport.join(","),
    gender.join(","),
    age.join(","),
    type.join(","),
    competition_level.join(","),
    verified,
    hasIntent,
    page,
    limit,
    q
  ]);

  useEffect(() => {
    const fetchAvailableStates = async () => {
      try {
        const country = (searchParams.get("country") || "US").toUpperCase();
        const params = { country };

        if (sport.length > 0) params.sport = sport;
        if (gender.length > 0) params.gender = gender;
        if (age.length > 0) params.age = age;
        if (type.length > 0) params.type = type;
        if (competition_level.length > 0) {
          params.competition_level = competition_level;
        }

        const res = await api.get("/api/directory/states", { params });

        const rawStates = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.states)
            ? res.data.states
            : [];

        const normalizedStates = rawStates
          .map((s) => (typeof s === "string" ? s : s?.code))
          .filter(Boolean);

        setAvailableStates(normalizedStates);
      } catch (err) {
        console.error("Failed to fetch available states:", err);
        setAvailableStates([]);
      }
    };

    fetchAvailableStates();
  }, [
    searchParams,
    sport.join(","),
    gender.join(","),
    age.join(","),
    type.join(","),
    competition_level.join(","),
  ]);




  useEffect(() => {
    const fetchScopedFilters = async () => {
      try {
        const country = (searchParams.get("country") || "US").toUpperCase();

        const params = {
          country,
          ...(state && { state: state.toUpperCase() }),
          ...(county && { county }),
          ...(sport.length > 0 && { sport }),
          ...(gender.length > 0 && { gender }),
          ...(age.length > 0 && { age }),
          ...(type.length > 0 && { type }),
          ...(competition_level.length > 0 && {
            competition_level,
          }),
        };

        const res = await api.get("/api/directory/filters", { params });

        setScopedFilters({
          sports: res.data.sports || [],
          audiences: res.data.audiences || [],
          age_groups: res.data.age_groups || [],
          program_types: res.data.program_types || [],
        });
      } catch (err) {
        console.error("Failed to fetch scoped filters:", err);

        setScopedFilters({
          sports: allSports || [],
          audiences: allAudiences || [],
          age_groups: allAges || [],
          program_types: allPrograms || [],
        });
      }
    };

    fetchScopedFilters();
  }, [
    searchParams,
    state,
    county,
    sport.join(","),
    gender.join(","),
    age.join(","),
    type.join(","),
    competition_level.join(","),
  ]);
  if (!hasIntent) return null;
  // if (loading) return <div>Loading...</div>;
  const formatLabel = (value = "") =>
    value
      .replace("_plus", "+")
      .replace(/_/g, " ")
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");


  const typeMap = {
    league: "Leagues",
    club: "Clubs",
    camp: "Camps",
    clinic: "Clinics",
    tournament: "Tournaments",
    training: "Training",
    event: "Events",
    open_gym: "Open Gyms",
  };

  const normalizeAgeValue = (value = "") =>
    value.replace(/_/g, "-").toLowerCase();

  const ageLabel = (() => {
    if (!age.length) return "";

    const selectedAges = [...new Set(age.map(normalizeAgeValue))].sort();

    const youthAgeGroups = [
      "3-4",
      "5-6",
      "7-8",
      "9-10",
      "11-12",
      "13-14",
      "15-17",
    ].sort();

    const adultAgeGroups = [
      "18-plus",
      "21-plus",
      "30-plus",
      "35-plus",
      "40-plus",
      "45-plus",
      "50-plus",
      "55-plus",
      "60-plus",
    ].sort();

    const sameValues = (a, b) =>
      a.length === b.length && a.every((value, index) => value === b[index]);

    const isYouthOnly =
      selectedAges.length > 0 &&
      selectedAges.every((value) => youthAgeGroups.includes(value));

    const isAdultOnly =
      selectedAges.length > 0 &&
      selectedAges.every((value) => adultAgeGroups.includes(value));

    if (sameValues(selectedAges, youthAgeGroups)) return "Youth";
    if (sameValues(selectedAges, adultAgeGroups)) return "Adult";

    if (selectedAges.length === 1) {
      const value = selectedAges[0];

      if (value === "youth-all") return "Youth";
      if (value === "adult-all") return "Adult";

      if (adultAgeGroups.includes(value)) {
        const minAge = value.split("-")[0];
        return `Adult Ages ${minAge} and Over`;
      }

      if (youthAgeGroups.includes(value)) {
        return `Youth Ages ${value}`;
      }

      return formatLabel(age[0]);
    }

    if (isAdultOnly) return "Adult";
    if (isYouthOnly) return "Youth";

    return "";
  })();

  const getAudienceLabel = () => {
    if (gender.length !== 1) return "";

    const value = gender[0];

    if (value === "coed") return "Coed";

    if (ageLabel === "Youth") {
      if (value === "female") return "Girls";
      if (value === "male") return "Boys";
    }

    if (ageLabel === "Adult") {
      if (value === "female") return "Women's";
      if (value === "male") return "Men's";
    }

    if (value === "female") return "Women's";
    if (value === "male") return "Men's";

    return formatLabel(value);
  };

  const audienceLabel = getAudienceLabel();
  const sportLabel =
    sport.length === 1 ? formatLabel(sport[0]) : "";

  const typeLabel =
    type.length === 1 ? typeMap[type[0]] || `${formatLabel(type[0])}s` : "";

  const locationLabel =
    county && state
      ? ` in ${formatLabel(county)}, ${getStateName(state)}`
      : state
        ? ` in ${getStateName(state)}`
        : county
          ? ` in ${formatLabel(county)}`
          : "";

  const hasSpecificSport = sport.length === 1;
  const hasSpecificType = type.length === 1;

  let mainLabel = "Sports Programs";

  if (hasSpecificSport && hasSpecificType) {
    if (audienceLabel === "Coed" && ageLabel) {
      mainLabel = `${audienceLabel} ${ageLabel} ${sportLabel} ${typeLabel}`;
    } else if (audienceLabel) {
      mainLabel = `${audienceLabel} ${sportLabel} ${typeLabel}`;
    } else if (ageLabel) {
      mainLabel = `${ageLabel} ${sportLabel} ${typeLabel}`;
    } else {
      mainLabel = `${sportLabel} ${typeLabel}`;
    }
  } else if (hasSpecificSport) {
    if (audienceLabel === "Coed" && ageLabel) {
      mainLabel = `${audienceLabel} ${ageLabel} ${sportLabel} Programs`;
    } else if (audienceLabel) {
      mainLabel = `${audienceLabel} ${sportLabel} Programs`;
    } else if (ageLabel) {
      mainLabel = `${ageLabel} ${sportLabel} Programs`;
    } else {
      mainLabel = `${sportLabel} Programs`;
    }
  } else if (hasSpecificType) {
    if (audienceLabel === "Coed" && ageLabel) {
      mainLabel = `${audienceLabel} ${ageLabel} Sports ${typeLabel}`;
    } else if (audienceLabel) {
      mainLabel = `${audienceLabel} Sports ${typeLabel}`;
    } else if (ageLabel) {
      mainLabel = `${ageLabel} Sports ${typeLabel}`;
    } else {
      mainLabel = `Sports ${typeLabel}`;
    }
  } else {
    if (audienceLabel === "Coed" && ageLabel) {
      mainLabel = `${audienceLabel} ${ageLabel} Sports Programs`;
    } else if (audienceLabel) {
      mainLabel = `${audienceLabel} Sports Programs`;
    } else if (ageLabel) {
      mainLabel = `${ageLabel} Sports Programs`;
    } else {
      mainLabel = "Sports Programs";
    }
  }
  const title = `${mainLabel}${locationLabel} | SportingHip`;

  const description = hasSpecificSport
    ? `Find ${mainLabel.toLowerCase()}${locationLabel} on SportingHip. Browse local options, compare organizations, and discover the right fit for your age group and program type.`
    : `Find ${mainLabel.toLowerCase()}${locationLabel} on SportingHip. Browse local options by sport, age group, audience, and program type.`;

  const showInitialSpinner = !hasLoadedOnce && loading;
  if (showInitialSpinner) {
    return (
      <div className="directory-results-page-spinner">
        <div className="directory-results-spinner" />
      </div>
    );
  }

  const startResult = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const endResult = Math.min(page * limit, totalCount);

  return (
    <div className="directory-results-container">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>



      <DirectoryResultsFilters
        allSports={allSports}
        allAudiences={scopedFilters.audiences}
        allAges={scopedFilters.age_groups}
        allPrograms={scopedFilters.program_types}
        availableStates={availableStates}
        count={totalCount}
        viewMode={viewMode}
        setViewMode={setViewMode}
        county={county}
        stateName={getStateName(state)}
        stateCode={state}
        page={page}
        totalPages={totalPages}
      />

      <div className="directory-results-header" ref={resultsHeaderRef}>
        <h1 className="directory-results-title">
          {mainLabel}
          {locationLabel}
          <span className="directory-results-count-text">
            ({totalCount} listings)
          </span>
        </h1>
        <p className="directory-results-summary">
          {description}
        </p>
      </div>



      {!loading && !directoryLeagues.length && (
        <div className="no-results">
          No leagues found
        </div>
      )}

      {directoryLeagues.length > 0 && (
        <div
          className={
            viewMode === "grid"
              ? "directory-results-grid"
              : "directory-results-list"}>
          {directoryLeagues.map((l) =>
            viewMode === "grid" ? (
              <LeagueCard key={l.id} directoryLeague={l} />
            ) : (
              <LeagueListItem key={l.id} directoryLeague={l} />
            )
          )}
        </div>
      )}
      {/* {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-controls">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ← Prev
            </button>

            <span>Page {page} of {totalPages}</span>

            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>

          <div className="pagination-meta">
            Showing {start}–{end} of {totalCount} leagues
          </div>
        </div>
      )} */}
      {!loading && totalCount > 0 && (
        <div className="results-pagination-bar">
          <div className="results-pagination-summary">
            Showing {startResult} to {endResult} of {totalCount} results
          </div>

          {totalPages > 1 && (
            <div className="results-pagination">
              <div className="results-pagination-controls">
                {page > 1 && (
                  <NavLink
                    to={buildPageSearchWithLimit(page - 1)}
                    className="results-pagination-prev"
                  >
                    <span className="results-pagination-arrow">←</span>
                    <span className="results-pagination-text"> Previous</span>
                  </NavLink>
                )}

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((pageNum) => {
                    return (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      Math.abs(pageNum - page) <= 1
                    );
                  })
                  .map((pageNum, index, arr) => {
                    const prev = arr[index - 1];
                    const showDots = prev && pageNum - prev > 1;

                    return (
                      <span key={pageNum} className="results-pagination-number-group">
                        {showDots && <span className="results-pagination-dots">...</span>}
                        <NavLink
                          to={buildPageSearchWithLimit(pageNum)}
                          className={page === pageNum ? "active-page" : ""}
                        >
                          {pageNum}
                        </NavLink>
                      </span>
                    );
                  })}

                {page < totalPages && (
                  <NavLink
                    to={buildPageSearchWithLimit(page + 1)}
                    className="results-pagination-next"
                  >
                    <span className="results-pagination-text">Next</span>
                    <span className="results-pagination-arrow">→</span>
                  </NavLink>
                )}
              </div>
            </div>
          )}

          <div className="results-pagination-page-size">
            <span>Show</span>

            <select
              value={limit}
              onChange={(e) => {
                navigate(buildPageSearchWithLimit(1, Number(e.target.value)));
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
            </select>

            <span>per page</span>
          </div>
        </div>
      )}

    </div>
  );

}

export default DirectoryResults;
